import { useYetiSummerCollectionContract } from './useContract'
import { useSingleCallResult } from '../state/multicall/hooks'
import { JSBI } from '@yetiswap/sdk'
import { useTransactionAdder } from '../state/transactions/hooks'
import { useMemo } from 'react'
import { calculateGasMargin } from '../utils'
import { TransactionResponse } from '@ethersproject/providers'
import { NFTMarketPlaceCallBackState } from './useNFTMarketPlace'

export function useCalculatePrice(amount: number): number {
  const contract = useYetiSummerCollectionContract()
  const priceResult = useSingleCallResult(contract, 'calculatePrice', [amount])
  let result = 0

  if (!priceResult.loading && priceResult.result) {
    result = parseInt(JSBI.BigInt(priceResult.result?.[0]).toString(), 0)
  }
  return result
}

export function useCurrentPrice(): number {
  const contract = useYetiSummerCollectionContract()
  const priceResult = useSingleCallResult(contract, 'calculatePrice', [1])
  let result = 0

  if (!priceResult.loading && priceResult.result) {
    result = parseInt(JSBI.BigInt(priceResult.result?.[0]).toString(), 0)
  }
  return result
}

export function useGetTotalSupply(): number {
  const contract = useYetiSummerCollectionContract()
  const totalSupplyResult = useSingleCallResult(contract, 'totalSupply')
  let result = 0

  if (!totalSupplyResult.loading && totalSupplyResult.result) {
    result = parseInt(JSBI.BigInt(totalSupplyResult.result?.[0]).toString(), 0)
    result = result !== 0 ? result - 1 : result
  }
  return result
}

export function useHasSaleStarted(): boolean {
  const contract = useYetiSummerCollectionContract()
  const hasSaleStartedResult = useSingleCallResult(contract, 'hasSaleStarted')
  let result = false

  if (!hasSaleStartedResult.loading && hasSaleStartedResult.result) {
    result = hasSaleStartedResult.result?.[0]
  }
  return result
}

export function useClaimNFT(
  amount: number,
  price: string
): {
  claimNFTState: NFTMarketPlaceCallBackState
  claimNFTCallback: null | (() => Promise<string>)
  error: string | null
} {
  const addTransaction = useTransactionAdder()
  const contract = useYetiSummerCollectionContract()
  return useMemo(() => {
    if (!contract) {
      return {
        claimNFTState: NFTMarketPlaceCallBackState.INVALID,
        claimNFTCallback: null,
        error: 'Missing dependencies'
      }
    }

    const args = [amount]

    return {
      claimNFTState: NFTMarketPlaceCallBackState.VALID,
      claimNFTCallback: async function onBuyNFT(): Promise<string> {
        return await contract.estimateGas['claimNFT'](...args, { value: price }).then(estimatedGasLimit => {
          return contract
            .claimNFT(...args, { value: price, gasLimit: calculateGasMargin(estimatedGasLimit) })
            .then((response: TransactionResponse) => {
              return response.wait(1).then((e: any) => {
                let result = 'NFT Claimed Failed'
                if (e && e.status === 1) {
                  result = 'NFT Claim Successful'
                }
                addTransaction(response, {
                  summary: result
                })
              })
            })
            .catch((error: any) => {
              // if the user rejected the tx, pass this along
              if (error?.code === 4001) {
                console.error(`'Transaction rejected claimNFT.'`, error)
              } else {
                // otherwise, the error was unexpected and we need to convey that
                console.error(`NFT Buy failed`, error)
              }
              return error
            })
        })
      },
      error: null
    }
  }, [addTransaction, contract, price, amount])
}
